/**
 * Sync note metadata edits back to source FHIR resources.
 */
import type { MedplumClient } from '@medplum/core';
import type { Appointment, ChargeItem, Encounter } from '@medplum/fhirtypes';
import { VIDEO_MEETING_URL_EXT } from '@unify/fhir-model/constants';
import {
  applyMetadataBillingToChargeItem,
  applyMetadataStartEndToAppointment,
  attendanceLabelToClientAttendance,
  diffNoteMetadata,
} from '../noteMetadataMapping';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import {
  appointmentNoteLocksFulfillment,
  isSignedNoteLockError,
  writeAppointmentAttendance,
} from './attendanceFulfillment';
import { formatMedplumClientError } from './medplumClientErrors';
import { updateAppointmentFresh, updateResourceFresh } from '@/lib/telehealth/updateAppointmentFresh';

/** Drop UI-only fields (e.g. `_localHasNote`) before persisting Appointment to Medplum. */
function overlayScheduleFields(saved: Appointment, desired: Appointment): Appointment {
  const video = desired.extension?.find((extension) => extension.url === VIDEO_MEETING_URL_EXT);
  const extension = video
    ? [
      ...(saved.extension ?? []).filter((extension) => extension.url !== VIDEO_MEETING_URL_EXT),
      video,
    ]
    : saved.extension;
  return {
    ...saved,
    start: desired.start,
    end: desired.end,
    minutesDuration: desired.minutesDuration,
    appointmentType: desired.appointmentType,
    extension,
  };
}

function scheduleFieldsDiffer(saved: Appointment, patched: Appointment): boolean {
  return saved.start !== patched.start
    || saved.end !== patched.end
    || saved.minutesDuration !== patched.minutesDuration
    || JSON.stringify(saved.appointmentType) !== JSON.stringify(patched.appointmentType)
    || JSON.stringify(saved.extension) !== JSON.stringify(patched.extension);
}

export function stripLocalAppointmentFields(appointment: Appointment): Appointment {
  const cleaned = { ...appointment } as Record<string, unknown>;
  for (const key of Object.keys(cleaned)) {
    if (key.startsWith('_')) delete cleaned[key];
  }
  return cleaned as unknown as Appointment;
}

export interface SyncNoteMetadataInput {
  medplum: MedplumClient;
  before: NoteMetadataSnapshot | undefined;
  after: NoteMetadataSnapshot;
  appointment: Appointment;
  encounter?: Encounter | null;
  chargeItems?: ChargeItem[];
}

export interface SyncNoteMetadataResult {
  appointment?: Appointment;
  encounter?: Encounter;
  chargeItems?: ChargeItem[];
  changedFields: string[];
}

export async function syncNoteMetadataEditsToSources(
  input: SyncNoteMetadataInput,
): Promise<SyncNoteMetadataResult> {
  const changedFields = diffNoteMetadata(input.before, input.after);
  if (changedFields.length === 0) {
    return { changedFields: [] };
  }

  const result: SyncNoteMetadataResult = { changedFields };
  const { medplum, appointment, encounter, chargeItems = [] } = input;
  const syncFailures: string[] = [];

  // Date/start are read-only from Appointment; duration and other fields sync on save.
  const appointmentFields = ['durationMinutes', 'attendance', 'modality', 'locations'];
  if (changedFields.some((f) => appointmentFields.includes(f)) && appointment.id) {
    try {
      if (await appointmentNoteLocksFulfillment(medplum, appointment.id)) {
        // A signed note freezes attendance, status, and the rest of this Appointment write.
      } else {
        const label = attendanceLabelToClientAttendance(input.after.attendance);
        const attendanceChanged = changedFields.includes('attendance') && label != null;
        const saved = await writeAppointmentAttendance(medplum, {
          appointmentId: appointment.id,
          appointment,
          clinicianCancel: attendanceChanged && label === 'clinician-cancelled',
          // Every label that reaches here is resolved (attended, no-show or a cancel); a cancel has no entries.
          markManualSource: attendanceChanged && label !== 'clinician-cancelled',
          attendance:
            attendanceChanged && label !== 'clinician-cancelled'
              ? (appointment.participant ?? []).flatMap((participant) => {
                const ref = participant.actor?.reference;
                if (!ref?.startsWith('Patient/')) return [];
                return [{ participantReference: ref, attendanceStatus: label }];
              })
              : undefined,
        });
        const desired = applyMetadataStartEndToAppointment(appointment, input.after);
        const patched = overlayScheduleFields(saved, desired);
        // If-Match on a fresh read so this cannot clobber concurrent bot writes (e.g. Chime session state).
        result.appointment = scheduleFieldsDiffer(saved, patched)
          ? await updateAppointmentFresh(medplum, appointment.id, (fresh) =>
            stripLocalAppointmentFields(overlayScheduleFields(fresh, desired)),
          )
          : saved;
      }
    } catch (err) {
      if (!isSignedNoteLockError(err)) {
        syncFailures.push(`Appointment update failed (${formatMedplumClientError(err)})`);
      }
    }
  }

  const encounterFields = ['durationMinutes'];
  if (
    encounter?.id &&
    changedFields.some((f) => encounterFields.includes(f)) &&
    input.after.start
  ) {
    try {
      const start = input.after.start;
      const durationMs =
        input.after.durationMinutes != null ? input.after.durationMinutes * 60_000 : undefined;
      // Only `period`, on a fresh read with If-Match, so a session location (or anything else) saved
      // since this page loaded the Encounter is kept.
      result.encounter = await updateResourceFresh<Encounter>(medplum, 'Encounter', encounter.id, (fresh) => ({
        ...fresh,
        period: {
          ...(fresh.period ?? {}),
          start,
          end:
            durationMs != null && Number.isFinite(Date.parse(start))
              ? new Date(Date.parse(start) + durationMs).toISOString()
              : fresh.period?.end,
        },
      }));
    } catch (err) {
      syncFailures.push(`Encounter update failed (${formatMedplumClientError(err)})`);
    }
  }

  const billingFields = ['billingCodes', 'units'];
  if (changedFields.some((f) => billingFields.includes(f)) && chargeItems.length > 0) {
    const updatedCharges: ChargeItem[] = [];
    const billingRows = input.after.billingCodes ?? [];
    for (const ci of chargeItems) {
      if (!ci.id) continue;
      const row =
        billingRows.find((r) => r.chargeItemId === ci.id) ??
        billingRows[chargeItems.indexOf(ci)];
      if (!row) continue;
      const updated = applyMetadataBillingToChargeItem(ci, row);
      if (JSON.stringify(updated) !== JSON.stringify(ci)) {
        try {
          updatedCharges.push(await medplum.updateResource(updated));
        } catch (err) {
          syncFailures.push(
            `ChargeItem/${ci.id} update failed (${formatMedplumClientError(err)})`,
          );
        }
      }
    }
    if (updatedCharges.length > 0) result.chargeItems = updatedCharges;
  }

  if (syncFailures.length > 0) {
    throw new Error(syncFailures.join('; '));
  }

  return result;
}
